// ============================================================
// droppedFiles - every file a drop carries, with its path, folders walked.
//
// A dropped FOLDER arrives as one item whose `webkitGetAsEntry()` is a
// directory entry; its files are reached only by walking it. Two traps:
// the items are dead once the drop handler returns, so the entries are
// taken synchronously before anything awaits; and a directory reader hands
// back at most a batch at a time (100 in Chrome), so it is read until it
// returns nothing. Where the entry API is missing, the plain file list.
//
// The shapes below are the few members of the browser's FileSystemEntry
// API this reads - declared here so the walk depends on nothing wider, and
// a test can hand it plain objects (jsdom has neither class).
// ============================================================

/** A file or directory in a drop: `fullPath` is '/Top/Sub/x.png'. */
interface Entry {
  isFile: boolean;
  isDirectory: boolean;
  fullPath: string;
  file?(ok: (f: File) => void, fail: (e: unknown) => void): void;
  createReader?(): { readEntries(ok: (es: Entry[]) => void, fail: (e: unknown) => void): void };
}

/** What a drop event's `dataTransfer` offers. */
interface Dropped {
  items?: ArrayLike<{ webkitGetAsEntry?(): Entry | null }>;
  files: ArrayLike<File>;
}

/** A file and where it sat: an entry's `fullPath`, or its bare name. */
export interface PathedFile {
  file: File;
  path: string;
}

const fileOf = (entry: Entry) => new Promise<File>((ok, fail) => entry.file!(ok, fail));

/** Every child of a directory: read again until a read comes back empty. */
async function childrenOf(dir: Entry): Promise<Entry[]> {
  const reader = dir.createReader!();
  const all: Entry[] = [];
  for (;;) {
    const batch = await new Promise<Entry[]>((ok, fail) => reader.readEntries(ok, fail));
    if (batch.length === 0) return all;
    all.push(...batch);
  }
}

/** The files under these entries, depth first, in the order given. */
async function walk(entries: readonly Entry[]): Promise<PathedFile[]> {
  const out: PathedFile[] = [];
  for (const e of entries) {
    if (e.isFile) out.push({ file: await fileOf(e), path: e.fullPath });
    else if (e.isDirectory) out.push(...(await walk(await childrenOf(e))));
  }
  return out;
}

/**
 * The files a drop carries, each with its path. Call it inside the drop
 * handler: it takes the entries before its first await. Rejects when the
 * browser cannot read one.
 */
export function droppedFiles(dt: Dropped): Promise<PathedFile[]> {
  const items = Array.from(dt.items ?? []);
  const entries = items.map((i) => i.webkitGetAsEntry?.()).filter((e): e is Entry => !!e);
  if (entries.length === 0) return Promise.resolve(Array.from(dt.files, (file) => ({ file, path: file.name })));
  return walk(entries);
}
